import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  FileText, 
  Search, 
  RefreshCw, 
  ChevronDown, 
  ChevronUp,
  ExternalLink,
  Plus,
  CheckCircle,
  Clock,
  AlertCircle,
  Receipt,
  Filter,
  Download,
  Eye,
  Send,
  Mail
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { useToast } from '../../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

const InvoiceManagement = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [expandedInvoice, setExpandedInvoice] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [payments, setPayments] = useState([]);
  const [sendingEmail, setSendingEmail] = useState(null);
  const { toast } = useToast();

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterType !== 'all') {
        params.append('invoice_type', filterType);
      }
      
      const response = await axios.get(`${API}/api/invoices/all?${params.toString()}`);
      if (response.data.success) {
        setInvoices(response.data.data.invoices);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch invoices",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [filterType, toast]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/invoices/stats/summary`);
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch invoice stats:', error);
    }
  }, []);

  const fetchPayments = useCallback(async () => {
    try {
      // Get payments that don't have balance invoices yet
      const response = await axios.get(`${API}/api/payments/create-deposit?limit=100`);
      // For now, we'll use a simpler approach - list all payments
    } catch (error) {
      console.error('Failed to fetch payments:', error);
    }
  }, []);

  useEffect(() => {
    fetchInvoices();
    fetchStats();
  }, [fetchInvoices, fetchStats]);

  const handleCreateBalanceInvoice = async (invoice) => {
    setCreatingInvoice(true);
    try {
      const response = await axios.post(`${API}/api/invoices/create-balance-invoice`, {
        quote_reference: invoice.quote_reference,
        customer_name: invoice.customer_name,
        customer_email: invoice.customer_email,
        customer_address: invoice.customer_address || '',
        total_price: invoice.total_price,
        windows: invoice.windows,
        material: invoice.material,
        louvre_size: invoice.louvre_size,
        colour: invoice.colour
      });

      if (response.data.success) {
        toast({
          title: "Success",
          description: `Balance invoice created for ${invoice.quote_reference}`,
        });
        fetchInvoices();
        fetchStats();
        setShowCreateDialog(false);
      } else {
        toast({
          title: "Error",
          description: response.data.error || "Failed to create invoice",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.error || "Failed to create invoice",
        variant: "destructive"
      });
    } finally {
      setCreatingInvoice(false);
    }
  };

  const handleSendInvoiceEmail = async (invoice) => {
    setSendingEmail(invoice.invoice_id);
    try {
      const endpoint = invoice.type === 'balance' 
        ? `${API}/api/invoices/send-balance-invoice/${invoice.invoice_id}`
        : `${API}/api/invoices/send-deposit-confirmation/${invoice.invoice_id}`;
      
      const response = await axios.post(endpoint);

      if (response.data.success) {
        toast({
          title: "Email Sent",
          description: `Invoice email sent to ${invoice.customer_email}`,
        });
        fetchInvoices();
      } else {
        toast({
          title: "Error",
          description: response.data.error || "Failed to send email",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.error || "Failed to send email",
        variant: "destructive"
      });
    } finally {
      setSendingEmail(null);
    }
  };

  const filteredInvoices = invoices.filter(invoice => {
    const matchesSearch = 
      invoice.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.customer_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.quote_reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.invoice_id?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
  });

  // Group invoices by quote reference for easier viewing
  const groupedInvoices = filteredInvoices.reduce((acc, invoice) => {
    const ref = invoice.quote_reference || 'Unknown';
    if (!acc[ref]) {
      acc[ref] = {
        quote_reference: ref,
        customer_name: invoice.customer_name,
        customer_email: invoice.customer_email,
        total_price: invoice.total_price,
        invoices: []
      };
    }
    acc[ref].invoices.push(invoice);
    return acc;
  }, {});

  // Find deposit invoices that don't have balance invoices yet
  const depositOnlyQuotes = Object.values(groupedInvoices).filter(group => {
    const hasDeposit = group.invoices.some(inv => inv.type === 'deposit');
    const hasBalance = group.invoices.some(inv => inv.type === 'balance');
    return hasDeposit && !hasBalance;
  });

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'pending':
      case 'draft':
      case 'open':
        return <Clock className="w-4 h-4 text-amber-500" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      paid: 'bg-green-100 text-green-800',
      pending: 'bg-amber-100 text-amber-800',
      draft: 'bg-gray-100 text-gray-800',
      open: 'bg-blue-100 text-blue-800'
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-4 shadow-sm border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Receipt className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Deposit Invoices</p>
                <p className="text-xl font-bold text-gray-900">{stats.deposit_invoices}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-4 shadow-sm border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Balance Invoices</p>
                <p className="text-xl font-bold text-gray-900">{stats.balance_invoices}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-4 shadow-sm border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Paid</p>
                <p className="text-xl font-bold text-gray-900">{stats.paid_invoices}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-4 shadow-sm border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Pending</p>
                <p className="text-xl font-bold text-gray-900">{stats.pending_invoices}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Action Bar */}
      <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
        <div className="flex flex-1 gap-3 w-full md:w-auto">
          <div className="relative flex-1 md:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search invoices..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="invoice-search"
            />
          </div>
          
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-40" data-testid="invoice-filter">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="deposit">Deposit Only</SelectItem>
              <SelectItem value="balance">Balance Only</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="flex gap-2">
          {depositOnlyQuotes.length > 0 && (
            <Button 
              onClick={() => setShowCreateDialog(true)}
              className="bg-sage-600 hover:bg-sage-700"
              data-testid="create-balance-invoice-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Balance Invoice
            </Button>
          )}
          
          <Button variant="outline" onClick={() => { fetchInvoices(); fetchStats(); }}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Invoices List */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-6 h-6 animate-spin text-sage-500" />
            <span className="ml-2 text-gray-500">Loading invoices...</span>
          </div>
        ) : filteredInvoices.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">No invoices found</p>
            <p className="text-sm text-gray-400 mt-1">Invoices will appear here after deposit payments are completed</p>
          </div>
        ) : (
          <div className="divide-y">
            {filteredInvoices.map((invoice) => (
              <div key={invoice.invoice_id} className="hover:bg-gray-50 transition-colors">
                <div 
                  className="p-4 cursor-pointer"
                  onClick={() => setExpandedInvoice(expandedInvoice === invoice.invoice_id ? null : invoice.invoice_id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        invoice.type === 'deposit' ? 'bg-blue-100' : 'bg-purple-100'
                      }`}>
                        {invoice.type === 'deposit' ? (
                          <Receipt className="w-5 h-5 text-blue-600" />
                        ) : (
                          <FileText className="w-5 h-5 text-purple-600" />
                        )}
                      </div>
                      
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">
                            {invoice.invoice_number || invoice.invoice_id}
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(invoice.status)}`}>
                            {invoice.status}
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            invoice.type === 'deposit' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'
                          }`}>
                            {invoice.type === 'deposit' ? '60% Deposit' : '40% Balance'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500">
                          {invoice.customer_name} • {invoice.quote_reference}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      {/* Quick PDF Button */}
                      <a
                        href={`${API}/api/invoices/pdf/${invoice.invoice_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="p-2 text-sage-600 hover:text-sage-800 hover:bg-sage-50 rounded-lg transition-colors"
                        title="View PDF"
                        data-testid={`quick-pdf-${invoice.invoice_id}`}
                      >
                        <FileText className="w-5 h-5" />
                      </a>
                      
                      <div className="text-right">
                        <p className="font-semibold text-gray-900">{formatCurrency(invoice.amount)}</p>
                        <p className="text-xs text-gray-500">{formatDate(invoice.created_at)}</p>
                      </div>
                      
                      {expandedInvoice === invoice.invoice_id ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Expanded Details */}
                {expandedInvoice === invoice.invoice_id && (
                  <div className="px-4 pb-4 pt-0">
                    <div className="bg-gray-50 rounded-lg p-4 ml-14">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500">Customer Email</p>
                          <p className="font-medium">{invoice.customer_email}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Total Quote</p>
                          <p className="font-medium">{formatCurrency(invoice.total_price)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Material</p>
                          <p className="font-medium">{invoice.material || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Payment ID</p>
                          <p className="font-medium font-mono text-xs">{invoice.payment_id || 'N/A'}</p>
                        </div>
                      </div>
                      
                      {invoice.windows && invoice.windows.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <p className="text-sm text-gray-500 mb-2">Windows</p>
                          <div className="space-y-1">
                            {invoice.windows.map((window, idx) => (
                              <div key={idx} className="flex justify-between text-sm">
                                <span>{window.label} ({window.layout_code || 'N/A'})</span>
                                <span className="font-medium">{formatCurrency(window.price)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      <div className="mt-4 pt-4 border-t border-gray-200 flex flex-wrap gap-2">
                        {/* PDF Actions */}
                        <a 
                          href={`${API}/api/invoices/pdf/${invoice.invoice_id}`}
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-sage-600 hover:bg-sage-700 rounded-lg transition-colors"
                          data-testid={`view-pdf-${invoice.invoice_id}`}
                        >
                          <Eye className="w-4 h-4" />
                          View PDF
                        </a>
                        <a 
                          href={`${API}/api/invoices/download/${invoice.invoice_id}`}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-sage-700 bg-sage-100 hover:bg-sage-200 rounded-lg transition-colors"
                          data-testid={`download-pdf-${invoice.invoice_id}`}
                        >
                          <Download className="w-4 h-4" />
                          Download PDF
                        </a>
                        
                        {/* Send to Customer Button */}
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSendInvoiceEmail(invoice);
                          }}
                          disabled={sendingEmail === invoice.invoice_id}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                            invoice.type === 'balance' 
                              ? 'bg-amber-500 hover:bg-amber-600 text-white' 
                              : 'bg-blue-500 hover:bg-blue-600 text-white'
                          }`}
                          data-testid={`send-email-${invoice.invoice_id}`}
                        >
                          {sendingEmail === invoice.invoice_id ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <Send className="w-4 h-4" />
                          )}
                          Send to Customer
                        </Button>
                        
                        {/* Email Sent Badge */}
                        {invoice.email_sent && (
                          <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded self-center flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            Email Sent
                          </span>
                        )}
                        
                        {invoice.local_only && (
                          <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded self-center">
                            Local Record
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Balance Invoice Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Balance Invoice</DialogTitle>
            <DialogDescription>
              Select a quote to create a balance invoice for the remaining 40% payment.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {depositOnlyQuotes.length === 0 ? (
              <p className="text-center text-gray-500 py-4">
                No quotes available for balance invoicing. All deposits either haven't been paid yet or already have balance invoices.
              </p>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {depositOnlyQuotes.map((group) => {
                  const depositInvoice = group.invoices.find(inv => inv.type === 'deposit');
                  const balanceAmount = (depositInvoice?.total_price || 0) * 0.40;
                  
                  return (
                    <div 
                      key={group.quote_reference}
                      className="border rounded-lg p-4 hover:border-sage-300 cursor-pointer transition-colors"
                      onClick={() => handleCreateBalanceInvoice(depositInvoice)}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium">{group.customer_name}</p>
                          <p className="text-sm text-gray-500">{group.quote_reference}</p>
                          <p className="text-sm text-gray-500">{group.customer_email}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-500">Balance Due</p>
                          <p className="text-lg font-bold text-sage-700">{formatCurrency(balanceAmount)}</p>
                        </div>
                      </div>
                      
                      <div className="mt-3 pt-3 border-t flex justify-between items-center">
                        <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
                          Deposit Paid: {formatCurrency(depositInvoice?.amount)}
                        </span>
                        <Button 
                          size="sm" 
                          disabled={creatingInvoice}
                          className="bg-sage-600 hover:bg-sage-700"
                        >
                          {creatingInvoice ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Plus className="w-4 h-4 mr-1" />
                              Create Invoice
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InvoiceManagement;
